// @ts-nocheck
/**
 * API客户端
 * 统一的API调用封装，自动添加认证Token和错误处理
 *
 * 相关文档: docs/API接口.md
 */

import { authUtils } from './auth';
import { logger } from './logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface FetchOptions extends RequestInit {
  body?: any;
}

/**
 * 统一的API调用函数
 */
export async function apiCall<T = any>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const config: RequestInit = {
    method: 'GET',
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // 如果有body且不是FormData，转换为JSON
  if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
  }

  // 如果是FormData，移除Content-Type（让浏览器自动设置boundary）
  if (config.body instanceof FormData) {
    delete (config.headers as Record<string, string>)['Content-Type'];
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (response.status === 401) {
      logger.warn('登录已失效，跳转统一登录');
      authUtils.login();
      throw new Error('未登录');
    }

    if (response.status === 403) {
      throw new Error('没有权限访问');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '请求失败' }));
      throw new Error(errorData.detail || `请求失败: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error(`API调用失败 ${endpoint}:`, error);
    throw error;
  }
}

export function apiGet<T = any>(endpoint: string): Promise<T> {
  return apiCall<T>(endpoint, { method: 'GET' });
}

export function apiPost<T = any>(endpoint: string, data?: any): Promise<T> {
  return apiCall<T>(endpoint, {
    method: 'POST',
    body: data,
  });
}

export function apiDelete<T = any>(endpoint: string, data?: any): Promise<T> {
  return apiCall<T>(endpoint, {
    method: 'DELETE',
    body: data,
  });
}

export function apiPatch<T = any>(endpoint: string, data?: any): Promise<T> {
  return apiCall<T>(endpoint, {
    method: 'PATCH',
    body: data,
  });
}

export function apiPut<T = any>(endpoint: string, data?: any): Promise<T> {
  return apiCall<T>(endpoint, {
    method: 'PUT',
    body: data,
  });
}

export function apiUpload<T = any>(endpoint: string, formData: FormData): Promise<T> {
  return apiCall<T>(endpoint, {
    method: 'POST',
    body: formData,
  });
}

/**
 * 流式响应请求（用于 TTS、SSE 等场景）
 * 返回原始 Response 对象，调用方自行处理流
 */
export async function apiStream(endpoint: string, data?: any): Promise<Response> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    logger.warn('登录已失效，跳转统一登录');
    authUtils.login();
    throw new Error('未登录');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: '请求失败' }));
    throw new Error(errorData.detail || `请求失败: ${response.status}`);
  }

  return response;
}

/**
 * 获取带认证的请求头（供需要自定义 fetch 的场景使用）
 */
export function getAuthHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
  };
}

export default apiCall;
